import React, { Fragment, memo, ReactNode } from 'react'
import tw from 'twin.macro'
import { Outlet } from 'react-router-dom'
import { NoAccessCard } from 'src/components/shared/NoAccessCard'
import type { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasAnyPermission } from 'src/hooks/useHasAnyPermission'

interface Props {
  // Any one of these opens the route. Absent or empty = open to every internal user.
  anyOf: InternalPermission[] | undefined
  message?: string
  // Omitted when used as a pathless layout route — the matched child renders through <Outlet />.
  children?: ReactNode
}

/**
 * Route-level gate for deep links. Renders inside `AdminShell`, so the top nav and breadcrumbs
 * stay; only the page body becomes the "No access" card.
 */
const RequirePermissionComponent = ({ anyOf, message, children }: Props) => {
  const isPermitted = useHasAnyPermission(anyOf)

  if (!isPermitted) {
    return (
      <div css={tw`flex min-w-0 flex-1 flex-col p-6`} data-testid="require-permission-denied">
        <NoAccessCard message={message} />
      </div>
    )
  }

  if (children === undefined) {
    return <Outlet />
  }

  return <Fragment>{children}</Fragment>
}

export const RequirePermission = memo(RequirePermissionComponent)
